/*
 * Copyright © 2020-2025 Ocean Galaxy Inc. All Rights Reserved.
 * @Description: 处理数据管理 Hook（读取和保存）
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-12-09 20:29:17
 */

import React, { useCallback, useEffect, useRef } from 'react'
import { message } from 'antd'
import { WatermarkExtractionStatus } from '../../context/WatermarkBatchContext'
import { TestDataItem } from './useCSVUpload'
import { useUploadDataToOSS } from './useUploadDataToOSS'
import { WatermarkApplyStatus } from '../../hooks/useWatermarkApply'

interface UseProcessedDataParams {
  dataSource: TestDataItem[]
  currentPage: number
  pageSize: number
  processedDataKeysSet: Set<string>
  setProcessedDataKeysSet: React.Dispatch<React.SetStateAction<Set<string>>>
  updateDataSource: (updater: (prev: TestDataItem[]) => TestDataItem[]) => void
  checkCurrentPageStatus: () => void
  getCurrentPageData: () => TestDataItem[]
}

export const useProcessedData = ({
  dataSource,
  currentPage,
  pageSize,
  processedDataKeysSet,
  setProcessedDataKeysSet,
  updateDataSource,
  checkCurrentPageStatus,
  getCurrentPageData
}: UseProcessedDataParams) => {
  const {
    readDataFromOSS,
    uploadDataToOSS,
    generateKey,
    uploadKeyList
  } = useUploadDataToOSS()

  // 用于防止重复上传的ref（存储正在上传的key）
  const uploadingKeysRef = useRef<Set<string>>(new Set())

  // 用于防止重复读取的ref（存储已经读取过的key）
  const readDataKeysRef = useRef<Set<string>>(new Set())

  // 当数据源或当前页变化时，读取当前页的处理数据
  useEffect(() => {
    if (dataSource.length === 0) return

    const loadDataForCurrentPage = async () => {
      const currentPageData = getCurrentPageData()

      // 过滤出在key列表中存在的项（只读取存在的处理数据），并且还没有读取过的
      const itemsToRead = currentPageData.filter((item) => {
        const itemKey = generateKey(item.oldUrl || '', item.newUrl || '')
        return (
          processedDataKeysSet.has(itemKey) &&
          !readDataKeysRef.current.has(itemKey)
        )
      })

      if (itemsToRead.length === 0) {
        return
      }

      console.log(
        `当前页共 ${currentPageData.length} 条数据，其中 ${itemsToRead.length} 条需要读取处理数据`
      )

      // 标记这些项为正在读取，避免重复读取
      itemsToRead.forEach((item) => {
        const itemKey = generateKey(item.oldUrl || '', item.newUrl || '')
        readDataKeysRef.current.add(itemKey)
      })

      // 并发读取所有需要读取的处理数据
      const dataPromises = itemsToRead.map(async (item) => {
        try {
          const processedData = await readDataFromOSS({
            originalUrl: item.oldUrl || '',
            manualEditUrl: item.newUrl || ''
          })

          if (processedData) {
            return {
              key: item.key,
              remark: processedData.remark || '',
              isProcessed: processedData.isProcessed || false,
              processedBy: processedData.processedBy || undefined,
              autoUrl: processedData.autoUrl || undefined
            }
          }
          return null
        } catch (error) {
          console.error(`读取 ${item.key} 的处理数据失败:`, error)
          const itemKey = generateKey(item.oldUrl || '', item.newUrl || '')
          readDataKeysRef.current.delete(itemKey)
          return null
        }
      })

      const dataResults = await Promise.all(dataPromises)

      // 批量更新数据源
      updateDataSource((prev) => {
        const updated = [...prev]
        let hasChanges = false

        dataResults.forEach((result) => {
          if (result) {
            const index = updated.findIndex((d) => d.key === result.key)
            if (index !== -1) {
              const currentItem = updated[index]
              if (
                currentItem.remark !== result.remark ||
                currentItem.isProcessed !== result.isProcessed ||
                currentItem.processedBy !== result.processedBy ||
                currentItem.autoUrl !== result.autoUrl
              ) {
                updated[index] = {
                  ...currentItem,
                  remark: result.remark,
                  isProcessed: result.isProcessed,
                  processedBy: result.processedBy,
                  autoUrl: result.autoUrl || currentItem.autoUrl,
                  extractionStatus: result.autoUrl
                    ? WatermarkExtractionStatus.COMPLETED
                    : currentItem.extractionStatus,
                  applyStatus: result.autoUrl
                    ? WatermarkApplyStatus.EDITED
                    : currentItem.applyStatus
                }
                hasChanges = true
              }
            }
          }
        })

        if (hasChanges) {
          setTimeout(() => {
            checkCurrentPageStatus()
          }, 0)
        }

        return hasChanges ? updated : prev
      })
    }

    loadDataForCurrentPage()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataSource.length, currentPage, pageSize, processedDataKeysSet])

  // 保存处理数据的函数（包括备注和自动处理链接）
  const handleSaveRemark = useCallback(
    async (
      key: string,
      remark: string,
      isProcessed: boolean,
      processedBy?: string
    ) => {
      const currentRecord = dataSource.find((d) => d.key === key)
      if (!currentRecord) {
        console.log('未找到记录，key:', key)
        return
      }

      const originalUrl = currentRecord.oldUrl || ''
      const manualEditUrl = currentRecord.newUrl || ''
      const itemKey = generateKey(originalUrl, manualEditUrl)

      if (uploadingKeysRef.current.has(itemKey)) {
        console.log('该记录正在上传中，跳过重复上传:', {
          key,
          itemKey,
          originalUrl,
          manualEditUrl
        })
        return
      }

      uploadingKeysRef.current.add(itemKey)

      console.log('开始保存备注:', {
        recordKey: key,
        itemKey,
        originalUrl,
        manualEditUrl,
        remark,
        isProcessed,
        processedBy
      })

      try {
        const uploadParams: any = {
          originalUrl,
          manualEditUrl,
          remark: remark || '',
          isProcessed: isProcessed ? true : false
        }

        if (processedBy) {
          uploadParams.processedBy = processedBy
        }

        if (currentRecord.autoUrl) {
          uploadParams.autoUrl = currentRecord.autoUrl
        }

        console.log('开始上传处理数据到OSS:', {
          key,
          itemKey,
          remark,
          originalUrl,
          manualEditUrl,
          autoUrl: currentRecord.autoUrl
        })

        const ossUrl = await uploadDataToOSS(uploadParams)

        if (ossUrl) {
          console.log('处理数据已成功上传到OSS:', JSON.stringify(uploadParams))

          updateDataSource((prev) => {
            const updated = [...prev]
            const index = updated.findIndex((d) => d.key === key)
            if (index !== -1) {
              updated[index] = {
                ...updated[index],
                remark: remark,
                isProcessed: isProcessed,
                processedBy: processedBy
              }
            }
            return updated
          })

          setProcessedDataKeysSet((prev) => {
            const newSet = new Set(prev)
            if (!newSet.has(itemKey)) {
              newSet.add(itemKey)
              const allKeys: string[] = []
              newSet.forEach((k) => allKeys.push(k))
              uploadKeyList(allKeys).catch((error) => {
                console.error('更新数据key列表到OSS失败:', error)
              })
              console.log('数据key列表已更新到OSS，共', allKeys.length, '个key')
            }
            return newSet
          })
        }
      } catch (error) {
        console.error('上传处理数据到OSS失败:', error)
        message.warning('数据已保存，但上传到OSS失败')
      } finally {
        uploadingKeysRef.current.delete(itemKey)
      }
    },
    [
      dataSource,
      updateDataSource,
      uploadDataToOSS,
      generateKey,
      uploadKeyList,
      setProcessedDataKeysSet
    ]
  )

  return {
    handleSaveRemark
  }
}
